import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";

type RangeKey = "h" | "d" | "w" | "m";
type IntervalKey = "s" | "m" | "h" | "d";

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "h", label: "h" },
  { key: "d", label: "d" },
  { key: "w", label: "w" },
  { key: "m", label: "m" },
];

const INTERVAL_OPTIONS: { key: IntervalKey; label: string }[] = [
  { key: "s", label: "1s" },
  { key: "m", label: "1m" },
  { key: "h", label: "1h" },
  { key: "d", label: "1d" },
];

function applyOffset(date: Date, range: RangeKey, sign: 1 | -1): Date {
  const d = new Date(date);
  switch (range) {
    case "h":
      d.setHours(d.getHours() + sign);
      break;
    case "d":
      d.setDate(d.getDate() + sign);
      break;
    case "w":
      d.setDate(d.getDate() + sign * 7);
      break;
    case "m":
      d.setMonth(d.getMonth() + sign);
      break;
  }
  return d;
}

function formatNYDate(date: Date): string {
  const ny = new Date(
    date.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
  const y = ny.getFullYear();
  const mo = String(ny.getMonth() + 1).padStart(2, "0");
  const day = String(ny.getDate()).padStart(2, "0");
  const h = String(ny.getHours()).padStart(2, "0");
  const mi = String(ny.getMinutes()).padStart(2, "0");
  return `${y}-${mo}-${day} ${h}:${mi}`;
}

interface ChartLauncherModalProps {
  publishedAt: number;
  onSubmit: (
    ticker: string,
    startDate: string,
    endDate: string,
    interval: string,
  ) => void;
  onClose: () => void;
}

export const ChartLauncherModal: React.FC<ChartLauncherModalProps> = ({
  publishedAt,
  onSubmit,
  onClose,
}) => {
  const [ticker, setTicker] = useState("");
  const [range, setRange] = useState<RangeKey>("h");
  const [interval, setInterval] = useState<IntervalKey>("m");

  const baseDate = useMemo(() => new Date(publishedAt * 1000), [publishedAt]);

  const startDate = useMemo(
    () => formatNYDate(applyOffset(baseDate, range, -1)),
    [baseDate, range],
  );
  const endDate = useMemo(
    () => formatNYDate(applyOffset(baseDate, range, 1)),
    [baseDate, range],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim()) return;
    onSubmit(ticker.trim(), startDate, endDate, interval);
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg p-4 min-w-[340px]"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="form-group">
            <label htmlFor="chart-ticker">Ticker:</label>
            <input
              id="chart-ticker"
              type="text"
              autoFocus
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              placeholder="AAPL, SPY, etc."
            />
          </div>

          <div className="form-group">
            <label>Range:</label>
            <div className="flex gap-3">
              {RANGE_OPTIONS.map((opt) => (
                <label key={opt.key} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="chart-range"
                    checked={range === opt.key}
                    onChange={() => setRange(opt.key)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Interval:</label>
            <div className="flex gap-3">
              {INTERVAL_OPTIONS.map((opt) => (
                <label key={opt.key} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="chart-interval"
                    checked={interval === opt.key}
                    onChange={() => setInterval(opt.key)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="text-sm text-gray-500 flex gap-3 flex-wrap">
            <span><b>Ticker:</b> {ticker || "—"}</span>
            <span><b>Start:</b> {startDate}</span>
            <span><b>End:</b> {endDate}</span>
            <span><b>Interval:</b> {interval}</span>
          </div>

          <button type="submit" className="btn btn-primary mt-1">
            Open Chart
          </button>
        </form>
      </div>
    </div>,
    document.body,
  );
};
